import { z } from "zod";

import { getEnvironmentOrDefault } from "../utilities/environment";

/**
 * A custom Zod schema for MCP Server URLs.
 * These are string URLs that must point to localhost or *.vylor.com.
 * In the "local-dev" environment, bare hostnames with no TLD are also allowed
 * so that Docker Compose service names (e.g. "wallipedia") work locally.
 * We don't want users to plug in random remote MCP servers as this could leak
 * Vylor data.
 */
const McpServerUrlSchema = z
  .string()
  .url()
  .refine(
    (url) => {
      try {
        const parsed = new URL(url);
        const hostname = parsed.hostname.toLowerCase();

        // Check if it's localhost (various forms)
        const isLocalhost =
          hostname === "localhost" ||
          hostname === "127.0.0.1" ||
          hostname === "[::1]" ||
          hostname === "0.0.0.0";

        const isFirstPartyDomain =
          hostname === "vylor.com" || hostname.endsWith(".vylor.com");

        // In local-dev, we allow unqualified hostnames such as http://wallipedia:3456/mcp
        // this is convenient for local developers running MCP servers in Docker containers.
        const isLocalDev = getEnvironmentOrDefault() === "local-dev";
        const urlLacksQualifiedHostname = !hostname.includes(".");
        if (isLocalDev && urlLacksQualifiedHostname) return true;

        return isLocalhost || isFirstPartyDomain;
      } catch {
        return false;
      }
    },
    {
      message: "URL must point to localhost or a *.vylor.com domain",
    }
  );

/**
 * Represents a configuration object for an MCP server.
 * e.g.
 * {
 *   "url": "https://mcp-weather.kube2.research.vylor.com/mcp",
 *   "transport": "streamable_http",
 *   "oauth_scopes": "api://<guuid application id of client registration>/user"
 * }
 */
export const McpServerConfigSchema = z.object({
  url: McpServerUrlSchema,
  transport: z.string(),
  oauth_scopes: z.string().optional(),
});
export type McpServerConfig = z.infer<typeof McpServerConfigSchema>;

/**
 * Represents an object containing multiple MCP server configs.
 * e.g.
 * {
 *   "weather": {
 *     "url": "https://mcp-weather.kube2.research.vylor.com/mcp",
 *     "transport": "streamable_http"
 *   }
 * }
 */
export const McpConfigSchema = z.record(z.string(), McpServerConfigSchema);
export type McpConfig = z.infer<typeof McpConfigSchema>;

/**
 * Represents preload resources for MCP servers.
 * Maps server names to arrays of resource URIs to preload.
 * e.g.
 * {
 *   "wallipedia": ["jargon://all"]
 * }
 */
export const McpPreloadResourcesSchema = z.record(
  z.string(),
  z.array(z.string())
);
export type McpPreloadResources = z.infer<typeof McpPreloadResourcesSchema>;

/**
 * Represents a progress event from FastMCP.Context.report_progress().
 * https://gofastmcp.com/servers/progress
 *
 * MCP servers emit progress events like so:
 *   await ctx.session.send_progress_notification(
 *     ctx.request_context.meta.progressToken,
 *     0.3,
 *     1.0,
 *     "Making NWS request...",
 *     ctx.request_id,
 *   )
 *
 * These events come in through the same server-sent event stream as chat
 * completions. Appearing like:
 *
 *   data: {"type": "mcp_event", "event_type": "progress", "progress": 0.3,
 *   "message": "Making NWS request...", "progress_token": "session-01ca5fb8",
 *   "total": 1.0, "timestamp": 1761259458.5271099}

 */
export const McpProgressEventSchema = z.object({
  type: z.literal("mcp_event"),
  event_type: z.literal("progress"),
  /**
   * Current progress value (e.g., 24, 0.75, 1500)
   */
  progress: z.number(),
  /**
   * Optional total value (e.g., 100, 1.0, 2000).
   * When provided, clients may interpret this as enabling percentage calculation
   */
  total: z.number().nullable().optional(),
  /**
   * An optional progress message.
   * e.g. "Making NWS request..."
   */
  message: z.string().nullable().optional(),
  /**
   * Unique ID for a specific tool used in a specific session.
   * e.g. "session-01ca5fb8"
   */
  progress_token: z.union([z.string(), z.number()]).nullable().optional(),
  /**
   * Timestamp for this progress event in Unix time.
   * Note: this timestamp is in seconds since the epoch. To use with a JS Date,
   * you must multiply by 1000, i.e. `new Date(timestamp * 1000);`
   */
  timestamp: z.number(),
});

// Infer the TypeScript type from the schema
export type McpProgressEvent = z.infer<typeof McpProgressEventSchema>;

/**
 * Represents a log event from FastMCP.Context.info(), FastMCP.Context.debug(),
 * FastMCP.Context.warn(), or FastMCP.Context.error().
 * https://gofastmcp.com/servers/logging
 *
 * MCP servers emit log events like so:
 *  await ctx.debug(f"Successfully received and decoded JSON from {url}")
 *
 * These events come in through the same server-sent event stream as chat
 * completions. Appearing like:
 *
 *   data: {"type": "mcp_event", "event_type": "log", "level": "debug",
 *   "message": {"msg": "Making NWS API request to URL: https://api.weather.gov/alerts/active/area/NM",
 *   "extra": null}, "timestamp": 1761259458.527325, "progress_token": "session-01ca5fb8"}
 */
export const McpLogEventSchema = z.object({
  type: z.literal("mcp_event"),
  event_type: z.literal("log"),
  level: z.enum(["debug", "info", "warn", "error"]),
  /**
   * The message logged.
   * Looks like an object, but the FastMCP docs suggest it should be a string?
   * https://gofastmcp.com/servers/logging
   */
  message: z.union([
    z.string(),
    z.object({
      msg: z.string(),
      extra: z.unknown(),
    }),
  ]),
  /**
   * Timestamp for this log event in Unix time.
   * Note: this timestamp is in seconds since the epoch. To use with a JS Date,
   * you must multiply by 1000, i.e. `new Date(timestamp * 1000);`
   */
  timestamp: z.number(),
  /**
   * Unique ID for a specific tool used in a specific session.
   * e.g. "session-01ca5fb8"
   */
  progress_token: z.union([z.string(), z.number()]).nullable().optional(),
});

// Infer the TypeScript type from the schema
export type McpLogEvent = z.infer<typeof McpLogEventSchema>;

/**
 * MCP events that can be emitted during assistant responses
 */
export type McpEvent = McpProgressEvent | McpLogEvent;
